/**
 * RunsCard — журнал запусков фоновых задач: фильтр по задаче, страницы по
 * 20. Ошибка видна текстом — чтобы понять, что сломалось, без логов сервера.
 */

import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';

import {jobRunsQueryOptions} from '@services/system';
import {Button} from '@components/ui/Button';
import {cn} from '@lib/utils';

import {fmtDuration, fmtWhen, JOB_LABELS, jobLabel, STATUS_META} from '../System.utils';

const LIMIT = 20;

export const RunsCard = () => {
  const [job, setJob] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const {data, isFetching} = useQuery(jobRunsQueryOptions({job, limit: LIMIT, offset}));
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const handleJob = (v: string) => {
    setJob(v === '' ? null : v);
    setOffset(0);
  };

  return (
    <div className="rounded-[13px] border border-skr-line bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <div className="text-sm font-semibold">Журнал запусков</div>
        <select
          value={job ?? ''}
          onChange={(e) => handleJob(e.target.value)}
          className="h-9 rounded-[9px] border border-input bg-card px-2.5 text-[13px] outline-none focus:border-primary"
        >
          <option value="">Все задачи</option>
          {Object.entries(JOB_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>
      {items.length === 0 ? (
        <div className="border-t border-skr-hairline px-4 py-8 text-center text-[13px] text-muted-foreground">
          {isFetching ? 'Загружаем…' : 'Запусков нет'}
        </div>
      ) : (
        items.map((r) => (
          <div
            key={r.id}
            className="flex items-start gap-3 border-t border-skr-hairline px-4 py-2.5"
          >
            <span
              className={cn(
                'mt-0.5 shrink-0 rounded-full px-2 py-px text-[11px] font-semibold',
                STATUS_META[r.status].className,
              )}
            >
              {STATUS_META[r.status].label}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold">{jobLabel(r.jobName)}</div>
              {r.detail !== null && (
                <div
                  className={cn(
                    'break-words text-[12px]',
                    r.status === 'failed' ? 'text-skr-danger' : 'text-muted-foreground',
                  )}
                >
                  {r.detail}
                </div>
              )}
            </div>
            <div className="shrink-0 text-right font-mono text-[11.5px] text-muted-foreground">
              <div>{fmtWhen(r.startedAt)}</div>
              <div>{fmtDuration(r.durationMs)}</div>
            </div>
          </div>
        ))
      )}
      {total > LIMIT && (
        <div className="flex items-center justify-center gap-3 border-t border-skr-hairline py-2.5">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
          >
            Новее
          </Button>
          <span className="font-mono text-[12px] text-muted-foreground">
            {offset + 1}–{Math.min(offset + LIMIT, total)} из {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            disabled={offset + LIMIT >= total}
            onClick={() => setOffset((o) => o + LIMIT)}
          >
            Старее
          </Button>
        </div>
      )}
    </div>
  );
};
